import React, { useState, useEffect } from 'react';
import { 
  FileText, Calendar, Plus, Download, RefreshCw, Filter,
  CheckCircle, Clock, AlertTriangle, XCircle, Building,
  DollarSign, Users, TrendingUp, Eye, Trash2, Send, Edit, Check
} from 'lucide-react';
import { Card, Button, Input, Select, Modal, Badge, Table, ConfirmDialog, Loading, EmptyState, Textarea } from '../components/ui';
import { recibosService, departamentosService } from '../services/api';
import { formatMoney, formatDate, getLocalDate } from '../utils/formatters';
import toast from 'react-hot-toast';

const MESES = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' }
];

const TIPOS_RECIBO = [
  { value: 'CUOTA_MANTENIMIENTO', label: 'Cuota de Mantenimiento' },
  { value: 'EXTRAORDINARIO', label: 'Cobro Extraordinario' },
  { value: 'AGUA', label: 'Consumo de Agua' },
  { value: 'LUZ_AREAS_COMUNES', label: 'Luz Áreas Comunes' },
  { value: 'FONDO_RESERVA', label: 'Fondo de Reserva' },
  { value: 'MEJORAS', label: 'Mejoras del Edificio' },
  { value: 'OTRO', label: 'Otro' }
];

const ESTADOS_RECIBO = [
  { value: '', label: 'Todos los estados' },
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'VENCIDO', label: 'Vencido' },
  { value: 'PAGADO', label: 'Pagado' },
  { value: 'ANULADO', label: 'Anulado' }
];

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

// Calcular fecha de vencimiento por defecto (día 15 del mes seleccionado)
const getDefaultVencimiento = (mes, anio) => {
  return `${anio}-${String(mes).padStart(2, '0')}-15`;
};

const RecibosPage = () => {
  const [loading, setLoading] = useState(true);
  const [recibos, setRecibos] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pendientes: 0,
    vencidos: 0,
    pagados: 0,
    montoPendiente: 0
  });
  
  // Filtros
  const [filters, setFilters] = useState({
    estado: '',
    tipo: '',
    departamento_id: '',
    anio: currentYear
  });
  
  // Modales
  const [modalGenerar, setModalGenerar] = useState(false);
  const [modalCrear, setModalCrear] = useState(false);
  const [modalEditar, setModalEditar] = useState(false);
  const [modalDetalle, setModalDetalle] = useState(false);
  const [confirmAnular, setConfirmAnular] = useState(null);
  
  // Formularios
  const [formGenerar, setFormGenerar] = useState({
    mes: currentMonth,
    anio: currentYear,
    dia_vencimiento: 10
  });
  
  const [formCrear, setFormCrear] = useState({
    tipo: 'CUOTA_MANTENIMIENTO',
    seleccion_departamentos: 'todos', // 'todos', 'seleccionados', 'uno'
    departamentos: [],
    departamento_id: '',
    concepto: '',
    mes: currentMonth,
    anio: currentYear,
    monto: '',
    fecha_emision: getLocalDate(),
    fecha_vencimiento: getDefaultVencimiento(currentMonth, currentYear),
    observaciones: ''
  });

  const [formEditar, setFormEditar] = useState({
    concepto: '',
    monto: '',
    fecha_vencimiento: '',
    observaciones: ''
  });
  
  const [selectedRecibo, setSelectedRecibo] = useState(null);
  const [selectedDepartamentos, setSelectedDepartamentos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadRecibos();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const deptosRes = await departamentosService.getAll();
      setDepartamentos(deptosRes.data.data || []);
    } catch (error) {
      console.error('Error cargando departamentos:', error);
    }
  };

  const loadRecibos = async () => {
    setLoading(true);
    try {
      const response = await recibosService.getAll(filters);
      const data = response.data.data || [];
      setRecibos(data);
      
      // Calcular stats
      setStats({
        total: data.length,
        pendientes: data.filter(r => r.estado === 'PENDIENTE').length,
        vencidos: data.filter(r => r.estado === 'VENCIDO').length,
        pagados: data.filter(r => r.estado === 'PAGADO').length,
        montoPendiente: data
          .filter(r => r.estado === 'PENDIENTE' || r.estado === 'VENCIDO')
          .reduce((sum, r) => sum + parseFloat(r.monto), 0)
      });
    } catch (error) {
      toast.error('Error al cargar recibos');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerarCuotas = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await recibosService.generarCuotas(formGenerar);
      const { generados, existentes } = response.data.data;
      
      if (generados > 0) {
        toast.success(`Se generaron ${generados} recibos correctamente`);
      }
      if (existentes > 0) {
        toast.info(`${existentes} recibos ya existían para ese período`);
      }
      
      setModalGenerar(false);
      loadRecibos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al generar cuotas');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCrearRecibo = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Preparar datos según la selección
      const dataToSend = {
        tipo: formCrear.tipo,
        concepto: formCrear.concepto || `${TIPOS_RECIBO.find(t => t.value === formCrear.tipo)?.label} - ${MESES.find(m => m.value === parseInt(formCrear.mes))?.label} ${formCrear.anio}`,
        mes: parseInt(formCrear.mes),
        anio: parseInt(formCrear.anio),
        monto: parseFloat(formCrear.monto),
        fecha_emision: formCrear.fecha_emision,
        fecha_vencimiento: formCrear.fecha_vencimiento,
        observaciones: formCrear.observaciones || null
      };
      
      // Determinar departamentos según selección
      if (formCrear.seleccion_departamentos === 'todos') {
        dataToSend.todos_departamentos = true;
      } else if (formCrear.seleccion_departamentos === 'seleccionados') {
        dataToSend.departamentos = selectedDepartamentos;
      } else {
        dataToSend.departamento_id = formCrear.departamento_id;
      }
      
      const response = await recibosService.create(dataToSend);
      const { generados, existentes } = response.data.data;
      
      if (generados > 0) {
        toast.success(`Se crearon ${generados} recibos correctamente`);
      }
      if (existentes > 0) {
        toast.info(`${existentes} recibos ya existían`);
      }
      
      setModalCrear(false);
      resetFormCrear();
      loadRecibos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al crear recibo');
    } finally {
      setSubmitting(false);
    }
  };

  const resetFormCrear = () => {
    setFormCrear({
      tipo: 'CUOTA_MANTENIMIENTO',
      seleccion_departamentos: 'todos',
      departamentos: [],
      departamento_id: '',
      concepto: '',
      mes: currentMonth,
      anio: currentYear,
      monto: '',
      fecha_emision: getLocalDate(),
      fecha_vencimiento: getDefaultVencimiento(currentMonth, currentYear),
      observaciones: ''
    });
    setSelectedDepartamentos([]);
  };

  const handleOpenEditar = (recibo) => {
    setSelectedRecibo(recibo);
    setFormEditar({
      concepto: recibo.concepto,
      monto: recibo.monto,
      fecha_vencimiento: recibo.fecha_vencimiento?.split('T')[0] || '',
      observaciones: recibo.observaciones || ''
    });
    setModalEditar(true);
  };

  const handleEditarRecibo = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await recibosService.update(selectedRecibo.id, formEditar);
      toast.success('Recibo actualizado correctamente');
      setModalEditar(false);
      loadRecibos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al actualizar recibo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleDepartamento = (deptoId) => {
    setSelectedDepartamentos(prev => 
      prev.includes(deptoId)
        ? prev.filter(id => id !== deptoId)
        : [...prev, deptoId]
    );
  };

  const handleSelectAllDepartamentos = () => {
    const allIds = departamentos.filter(d => d.estado !== 'INACTIVO').map(d => d.id);
    setSelectedDepartamentos(
      selectedDepartamentos.length === allIds.length ? [] : allIds
    );
  };

  const handleAnular = async () => {
    if (!confirmAnular) return;
    
    try {
      await recibosService.anular(confirmAnular.id);
      toast.success('Recibo anulado correctamente');
      setConfirmAnular(null);
      loadRecibos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al anular recibo');
    }
  };

  const getEstadoVariant = (estado) => {
    const variants = {
      PENDIENTE: 'warning',
      VENCIDO: 'danger',
      PAGADO: 'success',
      ANULADO: 'default'
    };
    return variants[estado] || 'default';
  };

  const getEstadoIcon = (estado) => {
    const icons = {
      PENDIENTE: Clock,
      VENCIDO: AlertTriangle,
      PAGADO: CheckCircle,
      ANULADO: XCircle
    };
    return icons[estado] || Clock;
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      CUOTA_MANTENIMIENTO: 'Cuota',
      EXTRAORDINARIO: 'Extraordinario',
      AGUA: 'Agua',
      LUZ_AREAS_COMUNES: 'Luz Común',
      FONDO_RESERVA: 'F. Reserva',
      MEJORAS: 'Mejoras',
      SANCION: 'Sanción',
      OTRO: 'Otro'
    };
    return tipos[tipo] || tipo;
  };

  const columns = [
    {
      header: 'Departamento',
      render: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center">
            <Building size={14} className="text-slate-400" />
          </div>
          <div>
            <p className="font-medium text-white">{row.departamento_numero}</p>
            <p className="text-xs text-slate-500">{row.propietario}</p>
          </div>
        </div>
      )
    },
    {
      header: 'Concepto',
      render: (row) => (
        <div>
          <p className="text-white">{row.concepto}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-slate-500">
              {MESES.find(m => m.value === row.mes)?.label} {row.anio}
            </span>
            {row.tipo !== 'CUOTA_MANTENIMIENTO' && (
              <Badge variant="purple" size="sm">{getTipoLabel(row.tipo)}</Badge>
            )}
          </div>
        </div>
      )
    },
    {
      header: 'Vencimiento',
      render: (row) => (
        <div className="text-sm">
          <p className="text-slate-300">{formatDate(row.fecha_vencimiento)}</p>
        </div>
      )
    },
    {
      header: 'Monto',
      render: (row) => (
        <span className="font-semibold text-white">{formatMoney(row.monto)}</span>
      )
    },
    {
      header: 'Estado',
      render: (row) => {
        const Icon = getEstadoIcon(row.estado);
        return (
          <div className="flex items-center gap-2">
            <Icon size={14} className={
              row.estado === 'PAGADO' ? 'text-emerald-400' :
              row.estado === 'VENCIDO' ? 'text-rose-400' :
              row.estado === 'PENDIENTE' ? 'text-amber-400' : 'text-slate-400'
            } />
            <Badge variant={getEstadoVariant(row.estado)} size="sm">
              {row.estado}
            </Badge>
          </div>
        );
      }
    },
    {
      header: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => { setSelectedRecibo(row); setModalDetalle(true); }}
            className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
            title="Ver detalle"
          >
            <Eye size={16} />
          </button>
          {row.estado !== 'PAGADO' && row.estado !== 'ANULADO' && (
            <>
              <button
                onClick={() => handleOpenEditar(row)}
                className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-blue-400 transition-colors"
                title="Editar"
              >
                <Edit size={16} />
              </button>
              <button
                onClick={() => setConfirmAnular(row)}
                className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400 transition-colors"
                title="Anular"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestión de Recibos</h1>
          <p className="text-slate-400">Genera y administra los recibos del edificio</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={loadRecibos} icon={RefreshCw}>
            Actualizar
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-500/20">
              <FileText className="text-blue-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-400">Total Recibos</p>
              <p className="text-xl font-bold text-white">{stats.total}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-amber-500/20">
              <Clock className="text-amber-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-400">Pendientes</p>
              <p className="text-xl font-bold text-amber-400">{stats.pendientes}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-rose-500/20">
              <AlertTriangle className="text-rose-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-400">Vencidos</p>
              <p className="text-xl font-bold text-rose-400">{stats.vencidos}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-emerald-500/20">
              <CheckCircle className="text-emerald-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-400">Pagados</p>
              <p className="text-xl font-bold text-emerald-400">{stats.pagados}</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 col-span-2 lg:col-span-1">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-purple-500/20">
              <DollarSign className="text-purple-400" size={20} />
            </div>
            <div>
              <p className="text-xs text-slate-400">Por Cobrar</p>
              <p className="text-xl font-bold text-purple-400">{formatMoney(stats.montoPendiente)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Acciones Rápidas */}
      <Card className="p-4 bg-gradient-to-r from-blue-500/5 to-emerald-500/5 border-blue-500/20">
        <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
          <TrendingUp size={18} className="text-blue-400" />
          Acciones Rápidas
        </h3>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => setModalGenerar(true)} icon={Calendar}>
            Generar Cuotas Mensuales
          </Button>
          <Button variant="secondary" onClick={() => setModalCrear(true)} icon={Plus}>
            Recibo Extraordinario
          </Button>
          <Button variant="ghost" icon={Download}>
            Exportar Excel
          </Button>
        </div>
      </Card>

      {/* Filtros */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-slate-400" />
          <h3 className="font-medium text-white">Filtros</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Select
            value={filters.estado}
            onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
            options={ESTADOS_RECIBO}
            placeholder="Estado"
          />
          <Select
            value={filters.tipo}
            onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
            options={[{ value: '', label: 'Todos los tipos' }, ...TIPOS_RECIBO]}
            placeholder="Tipo"
          />
          <Select
            value={filters.departamento_id}
            onChange={(e) => setFilters({ ...filters, departamento_id: e.target.value })}
            options={[
              { value: '', label: 'Todos los departamentos' },
              ...departamentos.map(d => ({ value: d.id, label: `Dpto ${d.numero} - ${d.propietario}` }))
            ]}
            placeholder="Departamento"
          />
          <Select
            value={filters.anio}
            onChange={(e) => setFilters({ ...filters, anio: e.target.value })}
            options={[
              { value: 2023, label: '2023' },
              { value: 2024, label: '2024' },
              { value: 2025, label: '2025' }
            ]}
            placeholder="Año"
          />
        </div>
      </Card>

      {/* Tabla de Recibos */}
      <Card>
        <div className="p-4 border-b border-slate-700">
          <h3 className="font-semibold text-white">Recibos Emitidos</h3>
          <p className="text-sm text-slate-400">{recibos.length} recibos encontrados</p>
        </div>
        
        {loading ? (
          <div className="p-8">
            <Loading text="Cargando recibos..." />
          </div>
        ) : recibos.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="No hay recibos"
            description="No se encontraron recibos con los filtros seleccionados"
          />
        ) : (
          <Table columns={columns} data={recibos} />
        )}
      </Card>

      {/* Modal Generar Cuotas */}
      <Modal
        isOpen={modalGenerar}
        onClose={() => setModalGenerar(false)}
        title="Generar Cuotas Mensuales"
        size="md"
      >
        <form onSubmit={handleGenerarCuotas} className="space-y-4">
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <p className="text-sm text-blue-300">
              Se generarán recibos de cuota de mantenimiento para <strong>todos los departamentos activos</strong> del edificio.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Mes"
              value={formGenerar.mes}
              onChange={(e) => setFormGenerar({ ...formGenerar, mes: parseInt(e.target.value) })}
              options={MESES}
              required
            />
            <Select
              label="Año"
              value={formGenerar.anio}
              onChange={(e) => setFormGenerar({ ...formGenerar, anio: parseInt(e.target.value) })}
              options={[
                { value: 2024, label: '2024' },
                { value: 2025, label: '2025' }
              ]}
              required
            />
          </div>

          <Input
            label="Día de Vencimiento"
            type="number"
            min="1"
            max="28"
            value={formGenerar.dia_vencimiento}
            onChange={(e) => setFormGenerar({ ...formGenerar, dia_vencimiento: parseInt(e.target.value) })}
            helperText={`Los recibos vencerán el día ${formGenerar.dia_vencimiento} de ${MESES.find(m => m.value === formGenerar.mes)?.label}`}
            required
          />

          <div className="p-4 rounded-xl bg-slate-700/50">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-400">Departamentos activos:</span>
              <span className="text-white font-medium">{departamentos.filter(d => d.estado !== 'INACTIVO').length}</span>
            </div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-400">Cuota estándar:</span>
              <span className="text-white font-medium">S/ 350.00</span>
            </div>
            <div className="border-t border-slate-600 mt-2 pt-2 flex justify-between">
              <span className="text-slate-300 font-medium">Total estimado:</span>
              <span className="text-xl font-bold text-emerald-400">
                {formatMoney(departamentos.filter(d => d.estado !== 'INACTIVO').length * 350)}
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalGenerar(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting} icon={Calendar}>
              {submitting ? 'Generando...' : 'Generar Cuotas'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Crear Recibo - MEJORADO */}
      <Modal
        isOpen={modalCrear}
        onClose={() => { setModalCrear(false); resetFormCrear(); }}
        title="Crear Nuevo Recibo"
        size="lg"
      >
        <form onSubmit={handleCrearRecibo} className="space-y-4">
          {/* Tipo de Recibo */}
          <Select
            label="Tipo de Recibo"
            value={formCrear.tipo}
            onChange={(e) => setFormCrear({ ...formCrear, tipo: e.target.value })}
            options={TIPOS_RECIBO}
            required
          />

          {/* Selección de Departamentos */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Aplicar a:
            </label>
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setFormCrear({ ...formCrear, seleccion_departamentos: 'todos' })}
                className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  formCrear.seleccion_departamentos === 'todos'
                    ? 'bg-blue-500/20 border-blue-500 text-blue-300'
                    : 'border-slate-600 text-slate-400 hover:border-slate-500'
                }`}
              >
                Todos los Dptos
              </button>
              <button
                type="button"
                onClick={() => setFormCrear({ ...formCrear, seleccion_departamentos: 'seleccionados' })}
                className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  formCrear.seleccion_departamentos === 'seleccionados'
                    ? 'bg-blue-500/20 border-blue-500 text-blue-300'
                    : 'border-slate-600 text-slate-400 hover:border-slate-500'
                }`}
              >
                Seleccionar Varios
              </button>
              <button
                type="button"
                onClick={() => setFormCrear({ ...formCrear, seleccion_departamentos: 'uno' })}
                className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  formCrear.seleccion_departamentos === 'uno'
                    ? 'bg-blue-500/20 border-blue-500 text-blue-300'
                    : 'border-slate-600 text-slate-400 hover:border-slate-500'
                }`}
              >
                Solo Uno
              </button>
            </div>

            {/* Selector múltiple de departamentos */}
            {formCrear.seleccion_departamentos === 'seleccionados' && (
              <div className="border border-slate-600 rounded-lg p-3 max-h-48 overflow-y-auto">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-slate-400">
                    {selectedDepartamentos.length} seleccionados
                  </span>
                  <button
                    type="button"
                    onClick={handleSelectAllDepartamentos}
                    className="text-xs text-blue-400 hover:text-blue-300"
                  >
                    {selectedDepartamentos.length === departamentos.filter(d => d.estado !== 'INACTIVO').length 
                      ? 'Deseleccionar todos' 
                      : 'Seleccionar todos'}
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {departamentos.filter(d => d.estado !== 'INACTIVO').map(depto => (
                    <label 
                      key={depto.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        selectedDepartamentos.includes(depto.id)
                          ? 'bg-blue-500/20 text-blue-300'
                          : 'hover:bg-slate-700 text-slate-400'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDepartamentos.includes(depto.id)}
                        onChange={() => handleToggleDepartamento(depto.id)}
                        className="rounded border-slate-600"
                      />
                      <span className="text-sm">{depto.numero}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Selector único de departamento */}
            {formCrear.seleccion_departamentos === 'uno' && (
              <Select
                value={formCrear.departamento_id}
                onChange={(e) => setFormCrear({ ...formCrear, departamento_id: e.target.value })}
                options={departamentos.filter(d => d.estado !== 'INACTIVO').map(d => ({ 
                  value: d.id, 
                  label: `Dpto ${d.numero} - ${d.propietario}` 
                }))}
                placeholder="Seleccionar departamento..."
                required={formCrear.seleccion_departamentos === 'uno'}
              />
            )}
          </div>

          {/* Período (Mes y Año) */}
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Mes del Período"
              value={formCrear.mes}
              onChange={(e) => {
                const mes = parseInt(e.target.value);
                setFormCrear({ 
                  ...formCrear, 
                  mes,
                  fecha_vencimiento: getDefaultVencimiento(mes, formCrear.anio)
                });
              }}
              options={MESES}
              required
            />
            <Select
              label="Año"
              value={formCrear.anio}
              onChange={(e) => {
                const anio = parseInt(e.target.value);
                setFormCrear({ 
                  ...formCrear, 
                  anio,
                  fecha_vencimiento: getDefaultVencimiento(formCrear.mes, anio)
                });
              }}
              options={[
                { value: 2024, label: '2024' },
                { value: 2025, label: '2025' },
                { value: 2026, label: '2026' }
              ]}
              required
            />
          </div>

          {/* Concepto */}
          <Input
            label="Concepto (opcional)"
            value={formCrear.concepto}
            onChange={(e) => setFormCrear({ ...formCrear, concepto: e.target.value })}
            placeholder={`Se generará automáticamente: ${TIPOS_RECIBO.find(t => t.value === formCrear.tipo)?.label} - ${MESES.find(m => m.value === parseInt(formCrear.mes))?.label} ${formCrear.anio}`}
          />

          {/* Monto */}
          <Input
            label="Monto (S/)"
            type="number"
            step="0.01"
            min="0.01"
            value={formCrear.monto}
            onChange={(e) => setFormCrear({ ...formCrear, monto: e.target.value })}
            placeholder="Ej: 350.00"
            required
          />

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Fecha de Emisión"
              type="date"
              value={formCrear.fecha_emision}
              onChange={(e) => setFormCrear({ ...formCrear, fecha_emision: e.target.value })}
              required
            />
            <Input
              label="Fecha de Vencimiento"
              type="date"
              value={formCrear.fecha_vencimiento}
              onChange={(e) => setFormCrear({ ...formCrear, fecha_vencimiento: e.target.value })}
              required
            />
          </div>

          {/* Observaciones */}
          <Textarea
            label="Observaciones (opcional)"
            value={formCrear.observaciones}
            onChange={(e) => setFormCrear({ ...formCrear, observaciones: e.target.value })}
            placeholder="Información adicional sobre este recibo..."
            rows={2}
          />

          {/* Resumen */}
          <div className="p-4 rounded-xl bg-slate-700/50">
            <h4 className="text-sm font-medium text-slate-300 mb-3">Resumen</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Departamentos:</span>
                <span className="text-white font-medium">
                  {formCrear.seleccion_departamentos === 'todos' 
                    ? `Todos (${departamentos.filter(d => d.estado !== 'INACTIVO').length})`
                    : formCrear.seleccion_departamentos === 'seleccionados'
                    ? `${selectedDepartamentos.length} seleccionados`
                    : formCrear.departamento_id 
                    ? `Dpto ${departamentos.find(d => d.id == formCrear.departamento_id)?.numero || ''}`
                    : '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Monto unitario:</span>
                <span className="text-white font-medium">{formCrear.monto ? formatMoney(parseFloat(formCrear.monto)) : '-'}</span>
              </div>
              {formCrear.monto && (
                <div className="border-t border-slate-600 pt-2 mt-2 flex justify-between">
                  <span className="text-slate-300 font-medium">Total a generar:</span>
                  <span className="text-xl font-bold text-emerald-400">
                    {formatMoney(parseFloat(formCrear.monto) * (
                      formCrear.seleccion_departamentos === 'todos' 
                        ? departamentos.filter(d => d.estado !== 'INACTIVO').length
                        : formCrear.seleccion_departamentos === 'seleccionados'
                        ? selectedDepartamentos.length
                        : 1
                    ))}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => { setModalCrear(false); resetFormCrear(); }}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={submitting || (formCrear.seleccion_departamentos === 'seleccionados' && selectedDepartamentos.length === 0)}
              icon={Plus}
            >
              {submitting ? 'Creando...' : 'Crear Recibo(s)'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Editar Recibo */}
      <Modal
        isOpen={modalEditar}
        onClose={() => setModalEditar(false)}
        title="Editar Recibo"
        size="md"
      >
        {selectedRecibo && (
          <form onSubmit={handleEditarRecibo} className="space-y-4">
            <div className="p-3 rounded-lg bg-slate-700/50 mb-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-white font-medium">Dpto {selectedRecibo.departamento_numero}</p>
                  <p className="text-sm text-slate-400">{MESES.find(m => m.value === selectedRecibo.mes)?.label} {selectedRecibo.anio}</p>
                </div>
                <Badge variant={getEstadoVariant(selectedRecibo.estado)}>
                  {selectedRecibo.estado}
                </Badge>
              </div>
            </div>

            <Input
              label="Concepto"
              value={formEditar.concepto}
              onChange={(e) => setFormEditar({ ...formEditar, concepto: e.target.value })}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Monto (S/)"
                type="number"
                step="0.01"
                min="0.01"
                value={formEditar.monto}
                onChange={(e) => setFormEditar({ ...formEditar, monto: e.target.value })}
                required
              />
              <Input
                label="Fecha de Vencimiento"
                type="date"
                value={formEditar.fecha_vencimiento}
                onChange={(e) => setFormEditar({ ...formEditar, fecha_vencimiento: e.target.value })}
                required
              />
            </div>

            <Textarea
              label="Observaciones (opcional)"
              value={formEditar.observaciones}
              onChange={(e) => setFormEditar({ ...formEditar, observaciones: e.target.value })}
              rows={2}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setModalEditar(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} icon={Check}>
                {submitting ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Modal Detalle */}
      <Modal
        isOpen={modalDetalle}
        onClose={() => setModalDetalle(false)}
        title="Detalle del Recibo"
      >
        {selectedRecibo && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-slate-700/50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Building size={20} className="text-slate-400" />
                  <div>
                    <p className="font-medium text-white">Dpto {selectedRecibo.departamento_numero}</p>
                    <p className="text-sm text-slate-400">{selectedRecibo.propietario}</p>
                  </div>
                </div>
                <Badge variant={getEstadoVariant(selectedRecibo.estado)} size="lg">
                  {selectedRecibo.estado}
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Concepto</p>
                <p className="text-white font-medium">{selectedRecibo.concepto}</p>
              </div>
              <div>
                <p className="text-slate-500">Tipo</p>
                <p className="text-white">{getTipoLabel(selectedRecibo.tipo)}</p>
              </div>
              <div>
                <p className="text-slate-500">Período</p>
                <p className="text-white">
                  {MESES.find(m => m.value === selectedRecibo.mes)?.label} {selectedRecibo.anio}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Vencimiento</p>
                <p className="text-white">{formatDate(selectedRecibo.fecha_vencimiento)}</p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
              <p className="text-sm text-slate-400">Monto</p>
              <p className="text-3xl font-bold text-emerald-400">{formatMoney(selectedRecibo.monto)}</p>
            </div>

            {selectedRecibo.estado !== 'PAGADO' && selectedRecibo.estado !== 'ANULADO' && (
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  variant="danger" 
                  onClick={() => { setModalDetalle(false); setConfirmAnular(selectedRecibo); }}
                  icon={Trash2}
                >
                  Anular Recibo
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Confirmar Anular */}
      <ConfirmDialog
        isOpen={confirmAnular !== null}
        onClose={() => setConfirmAnular(null)}
        onConfirm={handleAnular}
        title="Anular Recibo"
        message={`¿Estás seguro de anular el recibo "${confirmAnular?.concepto}" por ${formatMoney(confirmAnular?.monto || 0)}? Esta acción no se puede deshacer.`}
        confirmText="Sí, Anular"
        variant="danger"
      />
    </div>
  );
};

export default RecibosPage;
